import { StatusTag } from "components";
import dayjs from "dayjs";
import { HTMLAttributes, ReactNode } from "react";

export const isEmptyDate: (date?: string | null) => boolean = (date) => {
  if (!date) return true;
  return date.startsWith("0001-01-01");
};

export const waitTime = (time: number = 100) => {
  return new Promise<boolean>((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, time);
  });
};

export const getStr = (id?: number) => {
  if (!id) return "0";
  return id.toString();
};

export const isImage = (ext: string): boolean =>
  ext.search(/.(jpg|jpeg|png|gif)$/i) >= 0;

export const renderDate: (
  date?: Date | null | string | number,
  time?: boolean,
) => string = (date, time = false) => {
  if (!date) return "-";
  return isEmptyDate(date?.toString())
    ? "-"
    : dayjs(date).format(time ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD");
};

export const renderTransactionTypeTags: (type: string) => ReactNode = (
  type,
) => {
  switch (type) {
    case "box_purchase":
      return (
        <StatusTag
          status={{ color: "success", label: "Хөргөгчнөөс худалдаж авсан" }}
        />
      );
    case "box_refund":
      return (
        <StatusTag
          status={{ color: "error", label: "Хөргөгчөөс буцаалт хийсэн" }}
        />
      );
    case "machine_purchase":
      return (
        <StatusTag
          status={{ color: "blue", label: "Машинаас худалдаж авсан" }}
        />
      );
    case "machine_bonus":
      return (
        <StatusTag status={{ color: "purple", label: "Бонус нэмэгдсэн" }} />
      );
    case "survey":
      return <StatusTag status={{ color: "orange", label: "Тест бөглөсөн" }} />;
    case "wheel-point":
      return (
        <StatusTag status={{ color: "magenta", label: "Хүрд оноо хожсон" }} />
      );
    case "wheel-chance":
      return (
        <StatusTag status={{ color: "lime", label: "Хүрд ахин эргүүлэх" }} />
      );
    default:
      break;
  }
};

export const renderTransactionType: (type: string) => ReactNode = (type) => {
  switch (type) {
    case "box_purchase":
      return "Хөргөгчнөөс худалдаж авсан";
    case "box_refund":
      return "Хөргөгчөөс буцаалт хийсэн";
    case "machine_purchase":
      return "Машинаас худалдаж авсан";
    case "machine_bonus":
      return "Бонус нэмэгдсэн";
    case "survey":
      return "Тест бөглөсөн";
    case "wheel-point":
      return "Хүрд оноо хожсон";
    case "wheel-chance":
      return "Хүрд ахин эргүүлэх";
    default:
      break;
  }
};

export const formatAPIDate = (date?: any): string => {
  if (date) return dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
  return "";
};

export const MASKED_MONEY = "•••••";

// Module mirror of the header "mask currency" toggle (persisted in localStorage as
// "mask_currency"). renderMoney is a plain util called in ~130 places outside React,
// so it can't read the store hook — it reads this mirror instead. The store reducer
// keeps it in sync via setCurrencyMasked on every toggle, and the dashboard layout
// re-keys its content on the flag so all amounts re-render together.
let currencyMasked = (() => {
  try {
    return localStorage.getItem("mask_currency") === "1";
  } catch {
    return false;
  }
})();

export const setCurrencyMasked = (masked: boolean) => {
  currencyMasked = masked;
};

// Mask sensitive non-money fields (e.g. a customer register number) with the same
// header toggle used for currency, so they hide together.
export const maskSensitive = <T,>(value: T): T | string =>
  currencyMasked ? MASKED_MONEY : value;

export const renderMoney: (
  money?: number | null,
  symbol?: boolean,
) => string = (money, symbol = false) => {
  if (currencyMasked) return MASKED_MONEY + (symbol ? "₮" : "");
  if (!money) return "0";

  const truncated = Math.trunc(money * 100) / 100;
  const formatted = new Intl.NumberFormat("mn-MN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
  return formatted + (symbol ? "₮" : "");
};

export const tableCellFixed: (width: number) => {
  width: number;
  onCell: () => HTMLAttributes<HTMLElement>;
} = (width) => ({
  width,
  onCell: () => ({ style: { maxWidth: width, minWidth: width } }),
});

export const tablePagination = (
  {
    pageSize,
    current,
    ...rest
  }: Partial<{
    pageSize?: number;
    current?: number;
    [x: string]: any;
  }>,
  sort: any,
  withDefaultSorter = false,
) => {
  const defaultSorter = { created_at: "desc" };

  return {
    limit: pageSize || 20,
    page: (current || 1) - 1,
    sorter: withDefaultSorter ? { ...defaultSorter, ...sort } : { ...sort },
    ...rest,
  };
};

export const findFindByAttibute: <T extends any>(params: {
  code: keyof T;
  val: any;
  list: T[];
}) => T | null = ({ val, code, list }) => {
  const index = list.findIndex((item: any) => item[code] === val);
  if (index === -1) {
    return null;
  }
  return { ...(list[index] as any) };
};

export const renderPassengerType: (val: string) => string = (val: string) => {
  const mapToStr: any = {
    ADT: "Том хүн",
    CHD: "Хүүхэд",
    INF: "Няраа",
  };
  return mapToStr[val] ? mapToStr[val] : "-";
};

export const renderHHMM: (val?: string | null) => string = (val) => {
  if (!val) {
    return "-";
  }

  return `${val.substr(0, 2)}:${val.substr(2, 2)}`;
};

export const extractHHMM: (val?: string | number | null) => number[] = (
  val,
) => {
  if (!val) {
    return [0, 0];
  }
  if (typeof val === "string") {
    val = Number.parseInt(val);
  }

  if (val <= 0) {
    return [0, 0];
  }

  return [Math.floor(val / 60), Math.floor(val % 60)];
};

export const renderDateDDMMYY: (date: any) => string = (date) => {
  if (!date) return "-";
  return dayjs(date, "DDMMYY").format("YYYY-MM-DD");
};

export const extractDateTimeDiffrenceInHHMM = (
  startDate: string,
  endDate: string,
) => {
  const sDate = dayjs(startDate, "HHmm DDMMYY");
  const eDate = dayjs(endDate, "HHmm DDMMYY");
  const diff = eDate.diff(sDate, "minute");
  return extractHHMM(diff);
};

export const fileToUrl = (val?: string | null) => {
  if (!val) return "";
  return `${import.meta.env.VITE_FILE_GET_URL}${val}`;
};

export const subStr = (data: string) => {
  return data.substring(import.meta.env.VITE_FILE_GET_URL.length) ?? "";
};

export const getQueryVariable = (variable: string) => {
  const query = globalThis.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
};

const imageExtRegex = /\.(gif|jpe?g|tiff?|png|webp|bmp|svg)$/i;
const videoExtRegex = /\.(mp4|mov|wmv|flv|avi|webm|mkv)$/i;

export const isFileReviewAble = (name = ""): boolean => {
  if (imageExtRegex.test(name)) return true;
  if (videoExtRegex.test(name)) return true;
  return false;
};

export const isFileVideo = (name = ""): boolean => videoExtRegex.test(name);

export const isFileImage = (name = ""): boolean => imageExtRegex.test(name);

export const renderMMSS = (value: number) => {
  const sec_num = value;
  const hours = Math.floor(sec_num / 3600);
  const minutes = Math.floor(sec_num / 60);
  const seconds = Math.floor(sec_num - hours * 3600 - minutes * 60);

  let minutesStr = `${minutes}`;
  let secondsStr = `${seconds}`;

  if (minutes < 10) {
    minutesStr = `0${minutes}`;
  }
  if (seconds < 10) {
    secondsStr = `0${seconds}`;
  }
  return `${minutesStr}:${secondsStr}`;
};

export const weekDayToString = (val: number) => {
  switch (val) {
    case 0:
      return "Sunday";
    case 1:
      return "Monday";
    case 2:
      return "Tuesday";
    case 3:
      return "Wednesday";
    case 4:
      return "Thursday";
    case 5:
      return "Friday";
    case 6:
      return "Saturday";
    default:
      return "no-week-day";
  }
};

export const deliveryStatusColor = (
  total_qty: number,
  delivered_qty: number,
) => {
  if (delivered_qty === total_qty) return "green";
  if (delivered_qty === 0) return "red";
  return "orange";
};

export const isFieldDisabled = (
  user?: { org_id?: number; role?: { name?: string } } | null,
): boolean => {
  return !(user?.org_id === 1 && user?.role?.name === "admin");
};

export const getColorValue = (color: any): string => {
  return typeof color === "string"
    ? color
    : color?.metaColor?.hex ||
    `#${[color?.r ?? color?.metaColor?.r, color?.g ?? color?.metaColor?.g, color?.b ?? color?.metaColor?.b]
      .filter((x) => x !== undefined)
      .map((x) => Math.round(x).toString(16).padStart(2, "0"))
      .join("")}` || "";
}; 